/**
 * Main Application Component
 * Sets up routing and global providers
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  DashboardListPage,
  DashboardViewPage,
  DashboardCreatePage,
  DashboardSettingsPage,
  EndpointsPage,
  ConnectionsPage,
  TemplatesPage,
  SettingsPage,
  InfoPage,
  LoginPage,
  PlanningPage,
  ScenarioDetailPage,
  KPIDashboardPage,
  MachineDashboardPage,
  SchedulingPage,
  GanttPage,
} from './pages';
import { AuthProvider, AppSettingsProvider } from './contexts';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ProtectedRoute } from './components/ProtectedRoute';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppSettingsProvider>
        <WebSocketProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Redirect root to dashboards */}
            <Route path="/" element={<Navigate to="/dashboards" replace />} />

            {/* Protected Dashboard routes */}
            <Route
              path="/dashboards"
              element={
                <ProtectedRoute>
                  <DashboardListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboards/new"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboards/:id"
              element={
                <ProtectedRoute>
                  <DashboardViewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboards/:id/settings"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardSettingsPage />
                </ProtectedRoute>
              }
            />

            {/* AI Planning routes */}
            <Route
              path="/planning"
              element={
                <ProtectedRoute>
                  <PlanningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/planning/:id"
              element={
                <ProtectedRoute>
                  <ScenarioDetailPage />
                </ProtectedRoute>
              }
            />

            {/* KPI Dashboard */}
            <Route
              path="/kpi"
              element={
                <ProtectedRoute>
                  <KPIDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Machine Dashboard */}
            <Route
              path="/machines"
              element={
                <ProtectedRoute>
                  <MachineDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Scheduling Engine */}
            <Route
              path="/scheduling"
              element={
                <ProtectedRoute>
                  <SchedulingPage />
                </ProtectedRoute>
              }
            />

            {/* Gantt Chart */}
            <Route
              path="/gantt"
              element={
                <ProtectedRoute>
                  <GanttPage />
                </ProtectedRoute>
              }
            />

            {/* Admin-only routes */}
            <Route
              path="/endpoints"
              element={
                <ProtectedRoute adminOnly>
                  <EndpointsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/connections"
              element={
                <ProtectedRoute adminOnly>
                  <ConnectionsPage />
                </ProtectedRoute>
              }
            />
            {/* Legacy route — redirect to Settings > Status Mappings tab */}
            <Route
              path="/status-mappings"
              element={<Navigate to="/settings?tab=mappings" replace />}
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute adminOnly>
                  <TemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute adminOnly>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/info"
              element={
                <ProtectedRoute>
                  <InfoPage />
                </ProtectedRoute>
              }
            />

            {/* 404 fallback */}
            <Route
              path="*"
              element={
                <div className="flex items-center justify-center h-screen bg-gray-50">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-300 mb-4">404</h1>
                    <p className="text-gray-500 mb-4">Page not found</p>
                    <a
                      href="/dashboards"
                      className="text-[#45bdd6] hover:underline"
                    >
                      Go to Dashboards
                    </a>
                  </div>
                </div>
              }
            />
          </Routes>
        </BrowserRouter>
        </WebSocketProvider>
        </AppSettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
